import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Button } from "@/components/ui/button";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useDashboardRouter } from "@/hooks/useDashboardRouter";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@/lib/utils";
export type SidebarActiveSection = "actions" | "codesmith" | "usage" | "admin";
export const RAIL_ROW = cn("flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none", "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950", "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]");
export const RAIL_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
export function RailRowContents(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    badge,
    expanded: t1,
    hideLabelWhenCollapsed: t2
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const hideLabelWhenCollapsed = t2 === undefined ? true : t2;
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = collapsedAndRemoved && "hidden";
  const t5 = !badge && "pr-2";
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = cn("whitespace-nowrap text-left", t4, t5);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== label || $[6] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[5] = label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== badge || $[9] !== collapsedAndRemoved) {
    t8 = badge && !collapsedAndRemoved ? <span className="ml-auto mr-2 shrink-0">{badge}</span> : null;
    $[8] = badge;
    $[9] = collapsedAndRemoved;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(5);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] !== activeTheme || $[1] !== setTheme) {
    t0 = THEME_OPTIONS.map(t1 => {
      const {
        value,
        label,
        icon: Icon
      } = t1;
      const isActive = activeTheme === value;
      return <button key={value} type="button" aria-label={label} aria-pressed={isActive} onClick={() => setTheme(value)} className={cn("flex size-7 items-center justify-center rounded-md transition-colors", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]", isActive ? "bg-[#0000000f] text-neutral-950 dark:bg-white/6 dark:text-white" : "text-neutral-600 hover:bg-black/[0.04] hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white")}><Icon className="size-3.5" /></button>;
    });
    $[0] = activeTheme;
    $[1] = setTheme;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="-my-1 -mr-1 flex items-center gap-0.5">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
export function CollapsedIconTooltip(t0) {
  const $ = _c(7);
  const {
    label,
    enabled,
    children
  } = t0;
  if (!enabled) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <TooltipContent side="right">{label}</TooltipContent>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(15);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = hideLabelWhenCollapsed && !expanded;
  const t4 = active ? "page" : undefined;
  const t5 = active && RAIL_ROW_ACTIVE;
  let t6;
  if ($[1] !== t5) {
    t6 = cn(RAIL_ROW, t5, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Wrench className="size-[18px]" />;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== expanded || $[5] !== hideLabelWhenCollapsed) {
    t8 = <RailRowContents icon={t7} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[4] = expanded;
    $[5] = hideLabelWhenCollapsed;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== onClick || $[8] !== t4 || $[9] !== t6 || $[10] !== t8) {
    t9 = <Link href="/admin" onClick={onClick} aria-label="Admin" aria-current={t4} className={t6}>{t8}</Link>;
    $[7] = onClick;
    $[8] = t4;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t3 || $[13] !== t9) {
    t10 = <>{t2}<CollapsedIconTooltip label="Admin" enabled={t3}>{t9}</CollapsedIconTooltip></>;
    $[12] = t3;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(82);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const {
    user
  } = useContext(AuthContext);
  const handleLogout = useSidebarLogout();
  const router = useDashboardRouter();
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  let t3;
  if ($[0] !== currentOrg || $[1] !== router || $[2] !== usageHref) {
    t3 = () => {
      if (currentOrg) {
        router.prefetch(usageHref);
      }
    };
    $[0] = currentOrg;
    $[1] = router;
    $[2] = usageHref;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const prefetchUsage = t3;
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsMenuCloseTimer = useRef(null);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const cancelSettingsMenuClose = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const openSettingsMenu = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const scheduleSettingsMenuClose = t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => cancelSettingsMenuClose;
    t8 = [];
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== className) {
    t9 = cn("flex flex-col gap-1", className);
    $[9] = className;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = hideLabelWhenCollapsed && !expanded;
  const t11 = currentOrg ? true : null;
  let t12;
  if ($[11] !== onSectionClick) {
    t12 = onSectionClick?.("usage");
    $[11] = onSectionClick;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = activeSection === "usage" ? "page" : undefined;
  const t14 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t15;
  if ($[13] !== t14) {
    t15 = cn(RAIL_ROW, t14);
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <BarChart3 className="size-[18px]" />;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== expanded || $[17] !== hideLabelWhenCollapsed) {
    t17 = <RailRowContents icon={t16} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[16] = expanded;
    $[17] = hideLabelWhenCollapsed;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== prefetchUsage || $[20] !== t11 || $[21] !== t12 || $[22] !== t13 || $[23] !== t15 || $[24] !== t17 || $[25] !== usageHref) {
    t18 = <Link href={usageHref} prefetch={t11} onClick={t12} onMouseEnter={prefetchUsage} onFocus={prefetchUsage} aria-label="Usage and Billing" aria-current={t13} className={t15}>{t17}</Link>;
    $[19] = prefetchUsage;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t15;
    $[24] = t17;
    $[25] = usageHref;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t10 || $[28] !== t18) {
    t19 = <CollapsedIconTooltip label="Usage and Billing" enabled={t10}>{t18}</CollapsedIconTooltip>;
    $[27] = t10;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setSettingsMenuOpen(false);
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== settingsMenuOpen) {
    t21 = event => {
      if (settingsMenuOpen && event.button === 0 && !event.ctrlKey) {
        event.preventDefault();
      }
    };
    $[31] = settingsMenuOpen;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <SettingsIcon className="size-[18px]" />;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== expanded || $[35] !== hideLabelWhenCollapsed) {
    t23 = <RailRowContents icon={t22} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[34] = expanded;
    $[35] = hideLabelWhenCollapsed;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== settingsHref || $[38] !== t21 || $[39] !== t23) {
    t24 = <DropdownMenuTrigger asChild={true}><Link ref={settingsTriggerRef} href={settingsHref} aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onClick={t20} onPointerDown={t21}>{t23}</Link></DropdownMenuTrigger>;
    $[37] = settingsHref;
    $[38] = t21;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = event_1 => {
      const target = event_1.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_1.preventDefault();
      }
    };
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SettingsIcon className="size-[18px] shrink-0" />;
    t27 = <span>Settings</span>;
    $[42] = t26;
    $[43] = t27;
  } else {
    t26 = $[42];
    t27 = $[43];
  }
  let t28;
  if ($[44] !== settingsHref) {
    t28 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><Link href={settingsHref}>{t26}{t27}</Link></DropdownMenuItem>;
    $[44] = settingsHref;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  const t29 = currentOrg ? `/${currentOrg}/support` : "/support";
  let t30;
  let t31;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <LifeBuoy className="size-[18px] shrink-0" />;
    t31 = <span>Contact Support</span>;
    $[46] = t30;
    $[47] = t31;
  } else {
    t30 = $[46];
    t31 = $[47];
  }
  let t32;
  if ($[48] !== t29) {
    t32 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><Link href={t29}>{t30}{t31}</Link></DropdownMenuItem>;
    $[48] = t29;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer"><BookOpen className="size-[18px] shrink-0" /><span>Documentation...</span></a></DropdownMenuItem>;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  let t35;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <DropdownMenuItem onSelect={_temp2} className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent dark:hover:bg-transparent dark:focus:bg-transparent dark:data-[highlighted]:bg-transparent"><span className="flex items-center gap-2"><Palette className="size-[18px] shrink-0 text-neutral-600 dark:text-neutral-400" /><span>Appearance</span></span><AppearanceThemeToggle /></DropdownMenuItem>;
    t35 = <DropdownMenuSeparator />;
    $[51] = t34;
    $[52] = t35;
  } else {
    t34 = $[51];
    t35 = $[52];
  }
  let t36;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <LogOut className="size-[18px] shrink-0" />;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div>Log out</div>;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== user) {
    t38 = user?.username ? <div className="text-[12px] leading-[16px] text-neutral-800 dark:text-white/50">{user.username}</div> : null;
    $[55] = user;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  if ($[57] !== t38) {
    t39 = <div className="leading-tight">{t37}{t38}</div>;
    $[57] = t38;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  if ($[59] !== handleLogout || $[60] !== t39) {
    t40 = <DropdownMenuItem className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white" onClick={handleLogout}>{t36}{t39}</DropdownMenuItem>;
    $[59] = handleLogout;
    $[60] = t39;
    $[61] = t40;
  } else {
    t40 = $[61];
  }
  let t41;
  if ($[62] !== t28 || $[63] !== t32 || $[64] !== t40) {
    t41 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-36} className="w-[232px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp} onPointerDownOutside={t25}>{t28}{t32}{t33}{t34}{t35}{t40}</DropdownMenuContent>;
    $[62] = t28;
    $[63] = t32;
    $[64] = t40;
    $[65] = t41;
  } else {
    t41 = $[65];
  }
  let t42;
  if ($[66] !== settingsMenuOpen || $[67] !== t24 || $[68] !== t41) {
    t42 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t24}{t41}</DropdownMenu>;
    $[66] = settingsMenuOpen;
    $[67] = t24;
    $[68] = t41;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== expanded || $[71] !== hideLabelWhenCollapsed) {
    t43 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[70] = expanded;
    $[71] = hideLabelWhenCollapsed;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== expanded || $[74] !== statusAsHoverCard) {
    t44 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className="pt-1">{statusAsHoverCard ? <RailStatusHoverCard /> : <StatusWidgetMorph expanded={expanded} />}</div></BSErrorBoundary>;
    $[73] = expanded;
    $[74] = statusAsHoverCard;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t19 || $[77] !== t42 || $[78] !== t43 || $[79] !== t44 || $[80] !== t9) {
    t45 = <div className={t9}>{t19}{t42}{t43}{t44}</div>;
    $[76] = t19;
    $[77] = t42;
    $[78] = t43;
    $[79] = t44;
    $[80] = t9;
    $[81] = t45;
  } else {
    t45 = $[81];
  }
  return t45;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}
function _temp(event_0) {
  return event_0.preventDefault();
}